import React, { useState, useRef, useEffect } from 'react';
import { ShoppingCart, ArrowDown, TrendingUp, DollarSign, Users, Loader2 } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

const API_URL = config.API_URL;

/**
 * GA4EcommerceFunnel - Affiche le parcours d'achat e-commerce
 * Étapes: session_start -> view_item -> add_to_cart -> begin_checkout -> purchase
 */
const GA4EcommerceFunnel = ({
  token,
  propertyId,
  period,
  customStartDate,
  customEndDate,
  formatNumber,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'ga4_ecommerce_funnel'
}) => {
  const [funnelData, setFunnelData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (propertyId) {
      loadFunnelData();
    }
  }, [propertyId, period, customStartDate, customEndDate]);

  // Une réponse lente d'une période abandonnée ne doit pas écraser la période courante
  const abortRef = useRef(null);

  const loadFunnelData = async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/dashboard/ga4/ecommerce-funnel`,
        {
          propertyId,
          period,
          customStartDate,
          customEndDate
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        }
      );

      if (response.data.success) {
        setFunnelData(response.data.data);
      } else {
        setError(response.data.error || 'Erreur de chargement');
      }
    } catch (err) {
      // Requête remplacée par une plus récente : ne rien écraser
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      console.error('[GA4-FUNNEL] Erreur:', err);
      setError(err.response?.data?.error || 'Erreur de chargement du funnel');
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  };

  // Couleurs du funnel (du plus foncé au plus clair)
  const getStepColor = (index, total) => {
    const colors = [
      '#4F46E5', // Indigo - Session
      '#7C3AED', // Violet - View item
      '#A855F7', // Purple - Add to cart
      '#C084FC', // Light purple - Begin checkout
      '#10B981'  // Green - Purchase (success)
    ];
    return colors[index] || colors[0];
  };

  // Icône par étape
  const getStepIcon = (eventName) => {
    switch (eventName) {
      case 'session_start':
        return <Users size={16} />;
      case 'view_item':
        return <ShoppingCart size={16} />;
      case 'add_to_cart':
        return <ShoppingCart size={16} />;
      case 'begin_checkout':
        return <DollarSign size={16} />;
      case 'purchase':
        return <TrendingUp size={16} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{
        marginTop: '24px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        padding: '40px',
        textAlign: 'center'
      }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#6b7280' }} />
        <p style={{ marginTop: '12px', color: '#6b7280' }}>Chargement du parcours d'achat...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        marginTop: '24px',
        backgroundColor: '#fef2f2',
        borderRadius: '12px',
        border: '1px solid #fecaca',
        padding: '20px',
        color: '#dc2626'
      }}>
        {error}
      </div>
    );
  }

  if (!funnelData || !funnelData.steps || funnelData.steps.length === 0) {
    return null;
  }

  const { steps, summary } = funnelData;
  const firstStepUsers = steps[0]?.users || 0;

  return (
    <div className="ga4-ecommerce-funnel" style={{
      marginTop: '24px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb'
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1.1em',
          fontWeight: '600',
          color: '#1f2937',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <ShoppingCart size={20} />
          Parcours d'achat
        </h3>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Résumé rapide */}
          <div style={{
            display: 'flex',
            gap: '24px',
            fontSize: '0.875rem'
          }}>
            <div style={{ textAlign: 'center' }}>
              <span style={{ color: '#6b7280' }}>Sessions</span>
              <div style={{ fontWeight: '600', color: '#1f2937' }}>
                {formatNumber ? formatNumber(summary.totalSessions) : summary.totalSessions.toLocaleString()}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <span style={{ color: '#6b7280' }}>Achats</span>
              <div style={{ fontWeight: '600', color: '#10b981' }}>
                {formatNumber ? formatNumber(summary.totalPurchases) : summary.totalPurchases.toLocaleString()}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <span style={{ color: '#6b7280' }}>Taux conv.</span>
              <div style={{ fontWeight: '600', color: '#7c3aed' }}>
                {summary.overallConversionRate}%
              </div>
            </div>
            {summary.totalRevenue > 0 && (
              <div style={{ textAlign: 'center' }}>
                <span style={{ color: '#6b7280' }}>Revenu</span>
                <div style={{ fontWeight: '600', color: '#059669' }}>
                  {formatNumber ? formatNumber(summary.totalRevenue) : summary.totalRevenue.toLocaleString()}€
                </div>
              </div>
            )}
          </div>
          {toggleClientModuleVisibility && (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Parcours d'achat"
            />
          )}
        </div>
      </div>

      {/* Contenu du funnel */}
      <div style={{ padding: '24px 20px' }}>
        {/* Étapes du funnel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {steps.map((step, index) => {
            const barWidth = firstStepUsers > 0
              ? Math.max((step.users / firstStepUsers) * 100, 5)
              : 100;

            return (
              <div key={step.eventName}>
                {/* Indicateur de drop-off entre les étapes */}
                {index > 0 && step.dropOffRate > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px 0',
                    color: '#ef4444',
                    fontSize: '0.8em'
                  }}>
                    <ArrowDown size={14} style={{ marginRight: '4px' }} />
                    <span>-{step.dropOffRate}% d'abandon</span>
                  </div>
                )}

                {/* Étape */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '8px',
                  margin: '-8px',
                  borderRadius: '8px'
                }}>
                  {/* Numéro d'étape avec icône */}
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: getStepColor(index, steps.length),
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600',
                    fontSize: '0.85em',
                    flexShrink: 0
                  }}>
                    {getStepIcon(step.eventName)}
                  </div>

                  {/* Barre et informations */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px'
                    }}>
                      <span style={{
                        fontWeight: '500',
                        color: '#1f2937',
                        fontSize: '0.95em'
                      }}>
                        {step.label}
                      </span>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                        <span style={{
                          fontSize: '0.9em',
                          color: '#6b7280'
                        }}>
                          <strong style={{ color: '#374151' }}>
                            {formatNumber ? formatNumber(step.users) : step.users.toLocaleString()}
                          </strong> utilisateurs
                        </span>
                        <span style={{
                          fontSize: '0.9em',
                          fontWeight: '600',
                          color: index === 0 ? '#059669' : step.globalConversionRate >= 50 ? '#059669' : step.globalConversionRate >= 20 ? '#d97706' : '#dc2626',
                          backgroundColor: index === 0 ? '#d1fae5' : step.globalConversionRate >= 50 ? '#d1fae5' : step.globalConversionRate >= 20 ? '#fef3c7' : '#fee2e2',
                          padding: '2px 8px',
                          borderRadius: '12px'
                        }}>
                          {step.globalConversionRate}%
                        </span>
                      </div>
                    </div>

                    {/* Barre de progression */}
                    <div style={{
                      height: '24px',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <div style={{
                        width: `${barWidth}%`,
                        height: '100%',
                        backgroundColor: getStepColor(index, steps.length),
                        borderRadius: '6px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Résumé global */}
        {summary.totalPurchases > 0 && (
          <div style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#f0fdf4',
            borderRadius: '8px',
            border: '1px solid #bbf7d0'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div>
                <span style={{ color: '#166534', fontWeight: '500' }}>
                  Taux de conversion global
                </span>
                <div style={{ marginTop: '4px', fontSize: '0.85em', color: '#15803d' }}>
                  De "{steps[0]?.label}" à "{steps[steps.length - 1]?.label}"
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{
                  fontSize: '1.5em',
                  fontWeight: '700',
                  color: '#15803d'
                }}>
                  {summary.overallConversionRate}%
                </span>
                {summary.averageOrderValue > 0 && (
                  <div style={{ fontSize: '0.85em', color: '#166534', marginTop: '4px' }}>
                    Panier moyen: {formatNumber ? formatNumber(summary.averageOrderValue) : summary.averageOrderValue.toFixed(2)}€
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Message si pas de données e-commerce */}
        {summary.totalPurchases === 0 && firstStepUsers > 0 && (
          <div style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#fef3c7',
            borderRadius: '8px',
            border: '1px solid #fcd34d',
            color: '#92400e',
            fontSize: '0.9em'
          }}>
            <strong>Aucun achat enregistré sur cette période.</strong>
            <br />
            Vérifiez que les événements e-commerce (view_item, add_to_cart, begin_checkout, purchase) sont correctement configurés dans Google Analytics 4.
          </div>
        )}
      </div>
    </div>
  );
};

export default GA4EcommerceFunnel;
